import { useState, FormEvent } from "react";
import { Link } from "react-router-dom";

const SITE_URL = import.meta.env.VITE_SITE_URL || '';

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const elements = form.elements as HTMLFormControlsCollection;
    Array.from(elements).forEach((el) => {
      const input = el as HTMLInputElement | HTMLTextAreaElement;
      if (input.name) data.append(input.name, input.value);
    });
    try {
      await fetch("https://readdy.ai/api/form/d71gj9ghb0rqicn68he0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="pt-20">
      <title>Contact Cardiocraft CPR | CPR Classes Kissimmee FL | Get In Touch</title>
      <meta name="description" content="Contact Cardiocraft CPR in Kissimmee, FL. Questions about BLS, ACLS, PALS, or group CPR training? Reach our RN instructors by email, phone, or our contact form. Same-day classes available." />
      <meta name="keywords" content="contact Cardiocraft CPR, CPR school Kissimmee contact, BLS class inquiry Florida, CPR training Kissimmee phone" />
      <link rel="canonical" href={`${SITE_URL}/contact`} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'LocalBusiness',
            name: 'Cardiocraft CPR',
            url: `${SITE_URL}/contact`,
            telephone: '+14075643966',
            email: 'cardiocraftcpr@gmail.com',
            address: {
              '@type': 'PostalAddress',
              streetAddress: '1627 East Vine Street, Suite 203',
              addressLocality: 'Kissimmee',
              addressRegion: 'FL',
              postalCode: '34744',
              addressCountry: 'US',
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: 28.2919889,
              longitude: -81.4073246,
            },
            contactPoint: {
              '@type': 'ContactPoint',
              telephone: '+14075643966',
              contactType: 'customer service',
              areaServed: 'US',
              availableLanguage: 'English',
            },
          }),
        }}
      />
      {/* Hero */}
      <section className="py-20 bg-brand-purple">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <span className="inline-block border border-brand-gold/50 text-brand-gold text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-6">
            Get In Touch
          </span>
          <h1 className="font-serif text-5xl lg:text-6xl font-bold text-white mb-4">
            Contact Us Today to Get Started
          </h1>
          <p className="text-white/80 text-lg">
            Have questions about our CPR courses in Kissimmee? Ready to book?
            We&apos;d love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Main */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Info + Map */}
            <div>
              <h2 className="font-serif text-3xl font-bold text-brand-black mb-8">
                Visit Our Training Center
              </h2>

              <div className="space-y-6 mb-10">
                <div className="flex gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-brand-purple-pale rounded-lg text-brand-purple flex-shrink-0">
                    <i className="ri-map-pin-line text-xl" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-brand-gold uppercase tracking-widest mb-1">Address</p>
                    <p className="text-brand-black font-semibold">1627 East Vine Street, Suite 203</p>
                    <p className="text-gray-500">Kissimmee, FL 34744</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-brand-purple-pale rounded-lg text-brand-purple flex-shrink-0">
                    <i className="ri-mail-line text-xl" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-brand-gold uppercase tracking-widest mb-1">Email</p>
                    <a
                      href="mailto:cardiocraftcpr@gmail.com"
                      className="text-brand-purple font-semibold hover:underline cursor-pointer"
                    >
                      cardiocraftcpr@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-brand-purple-pale rounded-lg text-brand-purple flex-shrink-0">
                    <i className="ri-time-line text-xl" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-brand-gold uppercase tracking-widest mb-1">Availability</p>
                    <p className="text-brand-black font-semibold">Same-Day Classes Available</p>
                    <p className="text-gray-500">Contact us for current schedule and availability</p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="rounded-xl overflow-hidden border-4 border-brand-purple/20">
                <iframe
                  title="Cardiocraft CPR Location Kissimmee"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3510!2d-81.4073!3d28.2919!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88dd8040f34ca3b3%3A0x5a20c490f07ef069!2s1627+E+Vine+St%2C+Kissimmee%2C+FL+34744!5e0!3m2!1sen!2sus!4v1234567890"
                  width="100%"
                  height="320"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-brand-purple-pale rounded-2xl p-8 border border-brand-purple/10">
              <h2 className="font-serif text-2xl font-bold text-brand-black mb-2">
                Send Us a Message
              </h2>
              <p className="text-gray-500 text-sm mb-6">
                Questions about BLS certification, ACLS, PALS, or group training in Kissimmee?
                We typically respond within a few hours.
              </p>

              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 flex items-center justify-center bg-brand-purple-pale rounded-full mx-auto mb-4 text-brand-purple">
                    <i className="ri-check-double-line text-3xl" />
                  </div>
                  <h3 className="font-semibold text-brand-black text-lg mb-2">Message Sent!</h3>
                  <p className="text-gray-500 text-sm">We&apos;ll get back to you shortly.</p>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-brand-black mb-1.5">Name *</label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="Your name"
                        className="w-full h-12 px-4 bg-white border-2 border-gray-200 rounded-md text-sm focus:border-brand-purple focus:outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-brand-black mb-1.5">Email *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="you@example.com"
                        className="w-full h-12 px-4 bg-white border-2 border-gray-200 rounded-md text-sm focus:border-brand-purple focus:outline-none transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-brand-black mb-1.5">Subject *</label>
                    <input
                      type="text"
                      name="subject"
                      required
                      placeholder="Course inquiry, booking question..."
                      className="w-full h-12 px-4 bg-white border-2 border-gray-200 rounded-md text-sm focus:border-brand-purple focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-brand-black mb-1.5">Message *</label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="Tell us how we can help..."
                      className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-md text-sm focus:border-brand-purple focus:outline-none transition-colors resize-none"
                    />
                    <p className="text-gray-400 text-xs mt-1">Max 500 characters</p>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-brand-purple text-white font-bold py-3.5 rounded-md hover:bg-brand-purple-dark transition-colors cursor-pointer whitespace-nowrap disabled:opacity-70 flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <><i className="ri-loader-4-line animate-spin" /> Sending...</>
                    ) : (
                      <><i className="ri-send-plane-line" /> Send Message</>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Quick Book Banner */}
      <section className="py-12 bg-brand-gold">
        <div className="max-w-4xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="font-serif text-2xl font-bold text-brand-black">Ready to Get Certified?</p>
            <p className="text-brand-black/70 text-sm mt-1">Same-day BLS certification available in Kissimmee, FL</p>
          </div>
          <Link
            to="/booking#reserve-spot"
            className="bg-brand-black text-white font-bold px-8 py-3.5 rounded-md hover:bg-brand-gray transition-colors cursor-pointer whitespace-nowrap"
          >
            Book Now
          </Link>
        </div>
      </section>
    </main>
  );
}
